import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FiCopy, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { schedulerApiFetch } from '../../api/schedulerClient';
import DriverPicker from './DriverPicker';
import WindowTimeField from './WindowTimeField';
import {
  WEEKDAY_LABELS,
  errorsByWindow,
  formatHhmm,
  parseHhmm,
  validateWeekdayPlan,
} from './validateWindows';

const DASH = '—';
const AUTOSAVE_MS = 900;

function windowsFromSaved(saved) {
  const map = {};
  (saved || []).forEach((fleet) => {
    map[fleet.fleet_id] = (fleet.windows || []).map((w) => ({
      driver_id: w.driver_id || '',
      start: w.start || '',
      end: w.end || '',
      stale: !!w.stale,
    }));
  });
  return map;
}

function emptyWeek() {
  const byDay = {};
  const versions = {};
  for (let day = 0; day < 7; day += 1) {
    byDay[day] = {};
    versions[day] = 0;
  }
  return { byDay, versions };
}

/** Live fleets/drivers are static; tab switches reuse this catalog. */
function catalogFromPreview(body) {
  const { byDay, versions } = emptyWeek();
  const raw = body?.saved_by_weekday && typeof body.saved_by_weekday === 'object' ? body.saved_by_weekday : null;
  for (let day = 0; day < 7; day += 1) {
    const saved = raw
      ? (raw[String(day)] || [])
      : (Number(body?.weekday) === day ? (body?.saved || []) : []);
    byDay[day] = windowsFromSaved(saved);
    const listed = Number((body?.versions || {})[String(day)]);
    versions[day] = Number.isFinite(listed)
      ? listed
      : (Number(body?.weekday) === day ? (Number(body?.version) || 0) : 0);
  }
  return {
    byDay,
    versions,
    weekdaysSaved: Array.isArray(body?.weekdays_saved) ? body.weekdays_saved : [],
  };
}

function payloadFleets(windowsByFleet) {
  return Object.entries(windowsByFleet)
    .map(([fleet_id, windows]) => ({
      fleet_id,
      windows: (windows || []).map(({ driver_id, start, end }) => ({ driver_id, start, end })),
    }))
    .filter((fleet) => fleet.windows.length > 0);
}

function nextWindowTimes(windows) {
  const ends = (windows || []).map((w) => parseHhmm(w.end)).filter((m) => m !== null);
  const start = ends.length ? Math.min(Math.max(...ends) + 1, 1438) : 8 * 60;
  const end = Math.min(start + 60, 1439);
  return { start: formatHhmm(start), end: formatHhmm(end) };
}

async function readError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  const detail = body?.detail;
  if (detail && typeof detail === 'object') return { message: detail.message || fallback, detail };
  return { message: detail || body?.message || fallback, detail: null };
}

function CopyDayMenu({ weekday, disabled, onCopy }) {
  const [targets, setTargets] = React.useState([]);
  const toggle = (day) => setTargets((cur) => (cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]));
  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            disabled={disabled}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-md disabled:opacity-50"
          >
            <FiCopy className="w-3.5 h-3.5" />
            Copy day to…
          </PopoverButton>
          <PopoverPanel
            anchor={{ to: 'bottom end', gap: 4 }}
            className="z-[10000] w-56 p-2 bg-white border border-gray-200 rounded-lg shadow-lg"
          >
            {WEEKDAY_LABELS.map((label, day) => (day === weekday ? null : (
              <label key={label} className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded">
                <input type="checkbox" checked={targets.includes(day)} onChange={() => toggle(day)} />
                {label}
              </label>
            )))}
            <p className="px-2 pt-1 text-[11px] text-amber-700">Replaces the whole plan on those days.</p>
            <button
              type="button"
              disabled={!targets.length}
              onClick={async () => {
                close();
                await onCopy(targets);
                setTargets([]);
              }}
              className="mt-2 w-full px-3 py-1.5 text-xs font-semibold text-white bg-slate-900 rounded-md disabled:opacity-40"
            >
              Copy
            </button>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}

export default function FleetMappingPanel({ getAuthToken, defaultWeekday = 0, onFlushReady, onSaved }) {
  const [weekday, setWeekday] = React.useState(defaultWeekday);
  const [fleets, setFleets] = React.useState([]);
  const [drivers, setDrivers] = React.useState([]);
  const [windowsByFleet, setWindowsByFleet] = React.useState({});
  const [weekdaysSaved, setWeekdaysSaved] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [saveState, setSaveState] = React.useState('Saved');
  const [serverErrors, setServerErrors] = React.useState([]);
  const [warnings, setWarnings] = React.useState([]);
  const [notice, setNotice] = React.useState('');
  const [conflict, setConflict] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [onlyMapped, setOnlyMapped] = React.useState(false);

  const mountedRef = React.useRef(true);
  const loadGenRef = React.useRef(0);
  const timerRef = React.useRef(null);
  const dirtyRef = React.useRef(false);
  const versionRef = React.useRef(0);
  const weekdayRef = React.useRef(defaultWeekday);
  const windowsRef = React.useRef({});
  const savedByWeekdayRef = React.useRef(emptyWeek().byDay);
  const versionByWeekdayRef = React.useRef(emptyWeek().versions);
  const catalogReadyRef = React.useRef(false);
  const getAuthTokenRef = React.useRef(getAuthToken);
  const onSavedRef = React.useRef(onSaved);

  React.useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);
  React.useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenRef.current += 1;
    };
  }, []);

  const clientErrors = React.useMemo(
    () => validateWeekdayPlan(payloadFleets(windowsByFleet)),
    [windowsByFleet],
  );
  const rowErrors = React.useMemo(
    () => errorsByWindow([...clientErrors, ...serverErrors]),
    [clientErrors, serverErrors],
  );

  const putWeekday = React.useCallback(async (day, fleetsPayload, expectedVersion) => {
    const token = await getAuthTokenRef.current?.();
    return schedulerApiFetch('fleet-mapping', {
      method: 'PUT',
      bearerToken: token,
      body: { weekday: day, fleets: fleetsPayload, expected_version: expectedVersion },
    });
  }, []);

  /** Saves the current weekday if it has unsaved edits. Resolves true when nothing is left unsaved. */
  const flush = React.useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!dirtyRef.current) return true;
    const fleetsPayload = payloadFleets(windowsRef.current);
    if (validateWeekdayPlan(fleetsPayload).length) {
      if (mountedRef.current) setSaveState('Fix errors to save');
      return false;
    }
    if (mountedRef.current) setSaveState('Saving…');
    try {
      const response = await putWeekday(weekdayRef.current, fleetsPayload, versionRef.current);
      if (response.status === 409) {
        if (mountedRef.current) {
          setConflict(true);
          setSaveState('Changed elsewhere');
        }
        return false;
      }
      if (!response.ok) {
        const { message, detail } = await readError(response, 'Save failed');
        if (mountedRef.current) {
          setServerErrors(Array.isArray(detail?.errors) ? detail.errors : []);
          setNotice(message);
          setSaveState('Save failed');
        }
        return false;
      }
      const body = await response.json().catch(() => ({}));
      versionRef.current = Number(body.version) || versionRef.current + 1;
      versionByWeekdayRef.current[weekdayRef.current] = versionRef.current;
      savedByWeekdayRef.current[weekdayRef.current] = windowsRef.current;
      dirtyRef.current = false;
      onSavedRef.current?.(weekdayRef.current);
      if (mountedRef.current) {
        setServerErrors([]);
        setWarnings(Array.isArray(body.warnings) ? body.warnings : []);
        setNotice(body.skipped?.length ? `${body.skipped.length} row(s) skipped: fleet or driver is no longer active.` : '');
        setSaveState('Saved');
        setWeekdaysSaved((cur) => {
          const has = fleetsPayload.length > 0;
          const set = new Set(cur);
          if (has) set.add(weekdayRef.current); else set.delete(weekdayRef.current);
          return [...set].sort();
        });
      }
      return true;
    } catch (err) {
      if (mountedRef.current) setSaveState('Save failed');
      return false;
    }
  }, [putWeekday]);

  React.useEffect(() => {
    onFlushReady?.(flush);
    return () => {
      onFlushReady?.(null);
      flush();
    };
  }, [flush, onFlushReady]);

  const applyDay = React.useCallback((day) => {
    weekdayRef.current = day;
    const windows = savedByWeekdayRef.current[day] || {};
    windowsRef.current = windows;
    versionRef.current = versionByWeekdayRef.current[day] || 0;
    dirtyRef.current = false;
    if (!mountedRef.current) return;
    setWeekday(day);
    setWindowsByFleet(windows);
    setServerErrors([]);
    setConflict(false);
    setSaveState('Saved');
  }, []);

  const loadCatalog = React.useCallback(async (day) => {
    const gen = loadGenRef.current + 1;
    loadGenRef.current = gen;
    setLoading(true);
    setLoadError('');
    try {
      const token = await getAuthTokenRef.current?.();
      const response = await schedulerApiFetch(`fleet-mapping/preview?weekday=${day}`, {
        bearerToken: token,
        cacheBust: true,
      });
      if (!mountedRef.current || loadGenRef.current !== gen) return;
      if (!response.ok) {
        const { message } = await readError(response, 'Could not load Fleet Mapping');
        throw new Error(message);
      }
      const body = await response.json();
      if (!mountedRef.current || loadGenRef.current !== gen) return;
      const catalog = catalogFromPreview(body);
      savedByWeekdayRef.current = catalog.byDay;
      versionByWeekdayRef.current = catalog.versions;
      catalogReadyRef.current = true;
      setFleets(Array.isArray(body.fleets) ? body.fleets : []);
      setDrivers((Array.isArray(body.drivers) ? body.drivers : []).filter((d) => d.profile_role !== 'assist'));
      setWeekdaysSaved(catalog.weekdaysSaved);
      setWarnings([]);
      setNotice('');
      applyDay(day);
    } catch (err) {
      catalogReadyRef.current = false;
      if (mountedRef.current && loadGenRef.current === gen) setLoadError(err?.message || 'Could not load Fleet Mapping');
    } finally {
      if (mountedRef.current && loadGenRef.current === gen) setLoading(false);
    }
  }, [applyDay]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      await flush();
      if (cancelled) return;
      if (!catalogReadyRef.current) {
        await loadCatalog(defaultWeekday);
        return;
      }
      applyDay(defaultWeekday);
    })();
    return () => { cancelled = true; };
  }, [flush, loadCatalog, applyDay, defaultWeekday]);

  const switchWeekday = async (day) => {
    if (day === weekdayRef.current) return;
    const saved = await flush();
    if (!saved && dirtyRef.current) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`${WEEKDAY_LABELS[weekdayRef.current]} has unsaved changes that can't be saved yet. Discard them?`)) return;
    }
    applyDay(day);
  };

  const editFleet = (fleetId, updater) => {
    const next = { ...windowsRef.current, [fleetId]: updater(windowsRef.current[fleetId] || []) };
    windowsRef.current = next;
    dirtyRef.current = true;
    setWindowsByFleet(next);
    setServerErrors([]);
    setSaveState('Unsaved changes');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_MS);
  };

  const addWindow = (fleetId) => editFleet(fleetId, (list) => [...list, { driver_id: '', ...nextWindowTimes(list) }]);
  const changeWindow = (fleetId, index, patch) => editFleet(fleetId, (list) => list.map((w, i) => (i === index ? { ...w, ...patch, stale: false } : w)));
  const removeWindow = (fleetId, index) => editFleet(fleetId, (list) => list.filter((_, i) => i !== index));

  const copyTo = async (targets) => {
    const saved = await flush();
    if (!saved) {
      setNotice('Fix this day before copying it.');
      return;
    }
    const fleetsPayload = payloadFleets(windowsRef.current);
    const copiedWindows = windowsFromSaved(fleetsPayload);
    const failed = [];
    for (const day of targets) {
      try {
        const response = await putWeekday(day, fleetsPayload, versionByWeekdayRef.current[day] || 0);
        if (!response.ok) {
          failed.push(WEEKDAY_LABELS[day]);
          continue;
        }
        const body = await response.json().catch(() => ({}));
        versionByWeekdayRef.current[day] = Number(body.version) || (versionByWeekdayRef.current[day] || 0) + 1;
        savedByWeekdayRef.current[day] = copiedWindows;
      } catch (err) {
        failed.push(WEEKDAY_LABELS[day]);
      }
    }
    if (!mountedRef.current) return;
    const done = targets.filter((day) => !failed.includes(WEEKDAY_LABELS[day]));
    done.forEach((day) => onSavedRef.current?.(day));
    setWeekdaysSaved((cur) => [...new Set([...cur, ...(fleetsPayload.length ? done : [])])].sort());
    setNotice(failed.length ? `Copy failed for ${failed.join(', ')}.` : `Copied to ${done.map((d) => WEEKDAY_LABELS[d]).join(', ')}.`);
  };

  const driverName = React.useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.driver_id] = d; });
    return map;
  }, [drivers]);

  const needle = search.trim().toLowerCase();
  const visible = fleets.filter((fleet) => {
    if (onlyMapped && !(windowsByFleet[fleet.fleet_id] || []).length) return false;
    if (!needle) return true;
    return [fleet.fleet_id, fleet.reg_no, fleet.los_title, fleet.space_type_title, fleet.model].join(' ').toLowerCase().includes(needle);
  });
  const liveFleetIds = new Set(fleets.map((f) => f.fleet_id));
  const staleFleetIds = Object.keys(windowsByFleet).filter((id) => !liveFleetIds.has(id) && (windowsByFleet[id] || []).length);
  const mappedCount = fleets.filter((f) => (windowsByFleet[f.fleet_id] || []).length).length;
  const saveClass = saveState === 'Saved'
    ? 'bg-emerald-50 text-emerald-700'
    : ['Save failed', 'Fix errors to save', 'Changed elsewhere'].includes(saveState)
      ? 'bg-red-50 text-red-700'
      : 'bg-gray-100 text-gray-500';

  const renderWindows = (fleetId) => {
    const list = windowsByFleet[fleetId] || [];
    return (
      <div className="flex flex-wrap items-start gap-2">
        {list.map((w, index) => {
          const messages = rowErrors[`${fleetId}:${index}`] || [];
          const known = driverName[w.driver_id];
          const invalid = messages.length > 0;
          const note = [
            w.stale ? 'No longer active' : '',
            known && !known.has_garage ? 'No garage: return check skipped' : '',
            ...messages,
          ].filter(Boolean).join(' · ');
          return (
            <div key={index} className="flex w-[13.75rem] max-w-full shrink-0 flex-col gap-1">
              <DriverPicker
                value={w.driver_id}
                drivers={drivers}
                error={invalid}
                ariaLabel={`Driver for window ${index + 1}`}
                onChange={(driverId) => changeWindow(fleetId, index, { driver_id: driverId })}
              />
              <div className="flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <WindowTimeField
                    value={w.start}
                    error={invalid}
                    ariaLabel={`Start of window ${index + 1}`}
                    onChange={(start) => changeWindow(fleetId, index, { start })}
                  />
                </div>
                <span className="shrink-0 text-[10px] text-gray-400">to</span>
                <div className="min-w-0 flex-1">
                  <WindowTimeField
                    value={w.end}
                    error={invalid}
                    ariaLabel={`End of window ${index + 1}`}
                    onChange={(end) => changeWindow(fleetId, index, { end })}
                  />
                </div>
                <button
                  type="button"
                  aria-label="Remove window"
                  onClick={() => removeWindow(fleetId, index)}
                  className="flex h-8 w-7 shrink-0 items-center justify-center text-gray-400 hover:text-gray-700"
                >
                  <FiTrash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {note ? (
                <div className={`text-[10px] font-medium ${messages.length ? 'text-red-600' : 'text-amber-700'}`}>
                  {note}
                </div>
              ) : null}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => addWindow(fleetId)}
          className="inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 text-[10px] font-semibold tracking-wide text-gray-500 hover:bg-gray-50 hover:text-gray-800"
        >
          <FiPlus className="h-3 w-3" /> Add window
        </button>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col gap-2 p-3 bg-gray-50">
        <div className="relative z-40 flex flex-wrap items-center gap-2">
          <div className="flex gap-0.5 bg-gray-100/80 p-0.5 rounded-lg shrink-0">
            {WEEKDAY_LABELS.map((label, day) => (
              <button
                key={label}
                type="button"
                onClick={() => switchWeekday(day)}
                className={`relative px-2 py-1 rounded-md text-xs font-semibold ${weekday === day ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                {label.slice(0, 3)}
                {weekdaysSaved.includes(day) ? <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
              </button>
            ))}
          </div>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search rig no / LOS / fleet ID…"
            className="flex-1 min-w-[160px] h-8 px-2.5 py-1 text-xs bg-white border border-gray-200 rounded-md"
          />
          <label
            className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600 whitespace-nowrap"
            title="Hide fleets that have no driver windows on this weekday. Does not change the saved plan."
          >
            <input type="checkbox" checked={onlyMapped} onChange={(event) => setOnlyMapped(event.target.checked)} />
            Only mapped fleets
          </label>
          <span className="px-2 py-0.5 text-[11px] font-semibold tracking-wide text-gray-500 bg-white border border-gray-200 rounded-full whitespace-nowrap">
            {mappedCount}/{fleets.length} mapped
          </span>
          <span className={`px-2 py-0.5 text-[11px] font-semibold tracking-wide rounded-full whitespace-nowrap ${saveClass}`}>{saveState}</span>
          <CopyDayMenu weekday={weekday} disabled={loading || clientErrors.length > 0} onCopy={copyTo} />
          <button
            type="button"
            onClick={async () => { await flush(); loadCatalog(weekdayRef.current); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-semibold tracking-wide text-white bg-slate-900 rounded-md hover:bg-slate-800"
          >
            <FiRefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        {conflict ? (
          <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
            {WEEKDAY_LABELS[weekday]} was changed by someone else. Your edits were not saved.
            <button type="button" onClick={() => loadCatalog(weekdayRef.current)} className="ml-auto text-[11px] font-semibold underline">Reload</button>
          </div>
        ) : null}
        {notice ? <p className="text-[11px] text-amber-700">{notice}</p> : null}
        {warnings.length ? (
          <p className="text-[11px] text-amber-700">
            {warnings.length} driver(s) have no garage address. Their windows are planned without the return-to-garage check.
          </p>
        ) : null}
        {staleFleetIds.length ? (
          <p className="text-[11px] text-amber-700">
            {staleFleetIds.length} saved fleet(s) are no longer active and will be dropped on the next save.
          </p>
        ) : null}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading fleets…</div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{loadError}</div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto bg-white border border-gray-200 rounded-xl">
            <table className="min-w-[780px] w-full table-fixed text-xs border-separate border-spacing-0">
              <colgroup>
                <col className="w-[6rem]" />
                <col className="w-[7.5rem]" />
                <col className="w-[7.5rem]" />
                <col />
              </colgroup>
              <thead className="sticky top-0 z-20 bg-white text-left text-[10px] uppercase tracking-wider text-gray-400 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
                <tr>
                  <th className="sticky left-0 z-30 bg-white px-2.5 py-1.5 font-semibold">Rig No</th>
                  <th className="px-2.5 py-1.5 font-semibold">LOS</th>
                  <th className="px-2.5 py-1.5 font-semibold">Model</th>
                  <th className="px-2.5 py-1.5 font-semibold">Drivers · {WEEKDAY_LABELS[weekday]}</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2.5 py-8 text-center text-gray-500">No fleets match.</td>
                  </tr>
                ) : visible.map((fleet) => (
                  <tr key={fleet.fleet_id} className="align-top">
                    <td className="sticky left-0 z-10 bg-white px-2.5 py-1.5 border-t border-gray-100 font-semibold text-gray-800 whitespace-nowrap" title={fleet.reg_no || ''}>
                      {fleet.reg_no || DASH}
                    </td>
                    <td className="px-2.5 py-1.5 border-t border-gray-100 text-gray-700 truncate">
                      {fleet.los_title || DASH}
                      {fleet.space_type_title ? <span className="text-gray-400"> ({fleet.space_type_title})</span> : null}
                    </td>
                    <td className="px-2.5 py-1.5 border-t border-gray-100 text-gray-600 truncate">{fleet.model || DASH}</td>
                    <td className="px-2.5 py-1.5 border-t border-gray-100">{renderWindows(fleet.fleet_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
